// Grab as a runtime spherical joint between hand bone and target body.
//
// Flow per tick while the Grab button is held:
//   1. Search a cone from the character's torso forward for the nearest
//      opponent body. If empty, the cone half-angle widens (up to a cap);
//      on the next frame with an empty held button it resets.
//   2. Arms aim at the cone target via `getAimTarget()` — read by Character
//      to steer arm PD orientation.
//   3. Each hand that is close enough to the target body (within `grabReach`)
//      spawns a spherical joint, which is released the tick the button goes
//      up on that side.
//
// Gates layered on step 3:
//   - Punch held while one hand is already stuck ⇒ second hand won't latch
//     (the free hand is left to punch).
//   - Victim's `postWriggleLock > 0` ⇒ second hand refuses for the duration
//     (first hand is fine).
//
// Double-hand hold (both attacker hands on the SAME victim character):
//   - Per-tick up-impulse on every body of that victim, cancelling a
//     fraction of gravity → victim is lifted out in front. Feels like the
//     attacker is "holding them up".

import RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from '../config';
import { Bone, type Ragdoll } from './Ragdoll';
import type { Character } from './Character';

interface ActiveGrab {
  side: 'L' | 'R';
  joint: RAPIER.ImpulseJoint;
  victimBody: RAPIER.RigidBody;
  victimCharacter: Character | null;
}

export interface GrabAim {
  body: RAPIER.RigidBody;
  victim: Character | null;
}

const DEG2RAD = Math.PI / 180;

export class Grab {
  private active: ActiveGrab[] = [];
  private coneHalfAngle: number = CONFIG.character.grab.coneMinHalfAngleDeg * DEG2RAD;
  private aim: GrabAim | null = null;
  /** Seconds until this attacker may latch a new hand again. Set by
   *  `throwDoubleHold` so an immediate re-grab on a just-thrown victim is
   *  impossible — the victim gets a breathing window to move away. */
  private latchCooldown = 0;
  /** Surface-only re-latch cooldown. Set by `releaseSurfaceHolds` (pull-up
   *  release). Blocks the surface fallback only — character cone latches are
   *  unaffected, so a player can still grab an opponent during this window. */
  surfaceLatchCooldown = 0;
  /** Set of victim Characters this attacker currently has ≥1 hand on.
   *  Attackers notify victims on change so they can drive the wriggle bar. */
  private heldVictims = new Set<Character>();

  constructor(
    private world: RAPIER.World,
    private ragdoll: Ragdoll,
    private ownMembership: number,
    private getOpponentBodies: () => Set<RAPIER.RigidBody>,
    private lookupCharacter: (body: RAPIER.RigidBody) => Character | null
  ) {
    void this.ownMembership;
  }

  isGrabbing(): boolean { return this.active.length > 0; }
  count(): number { return this.active.length; }
  isHandStuck(side: 'L' | 'R'): boolean {
    return this.active.some(a => a.side === side);
  }
  /** True when both hands are stuck on the SAME victim character — the
   *  "lifting them up like they're nothing" state. */
  isDoubleHolding(): boolean {
    if (this.active.length !== 2) return false;
    const v0 = this.active[0]!.victimCharacter;
    const v1 = this.active[1]!.victimCharacter;
    return v0 != null && v0 === v1;
  }
  /** True when both hands are stuck on surfaces (non-character bodies). The
   *  two surface bodies may differ. Gate for the pull-up climbing mechanic. */
  isDoubleSurfaceHolding(): boolean {
    if (this.active.length !== 2) return false;
    return this.active[0]!.victimCharacter === null
        && this.active[1]!.victimCharacter === null;
  }
  doubleHoldVictim(): Character | null {
    return this.isDoubleHolding() ? this.active[0]!.victimCharacter : null;
  }
  /** Read-only view of currently-active grabs. Used by the pull-up driver
   *  to apply per-tick anchor force on grabbed hand bones. */
  activeGrabs(): ReadonlyArray<{ side: 'L' | 'R'; victimCharacter: Character | null }> {
    return this.active;
  }
  countOn(victim: Character): number {
    let n = 0;
    for (const a of this.active) if (a.victimCharacter === victim) n += 1;
    return n;
  }
  /** The body the arms are currently aiming at (null if no target found). */
  getAimTarget(): GrabAim | null { return this.aim; }

  /**
   * Drive a grab tick.
   *
   * @param heldL / heldR   per-hand Grab-button state
   * @param forwardX,Z     unit facing direction (for cone origin's forward axis)
   * @param punchHeld      if true AND one hand is already stuck, the other
   *                       hand won't latch this tick (it punches instead)
   * @param dt             sim dt in seconds
   */
  update(
    heldL: boolean,
    heldR: boolean,
    forwardX: number,
    forwardZ: number,
    punchHeld: boolean,
    dt: number
  ): void {
    // --- Release any grabs whose button is no longer held (per-hand). ---
    if (this.active.length > 0) {
      const keep: ActiveGrab[] = [];
      for (const a of this.active) {
        const wantHeld = a.side === 'L' ? heldL : heldR;
        if (wantHeld) keep.push(a);
        else this.world.removeImpulseJoint(a.joint, true);
      }
      this.active = keep;
    }

    if (this.latchCooldown > 0) this.latchCooldown = Math.max(0, this.latchCooldown - dt);
    if (this.surfaceLatchCooldown > 0) this.surfaceLatchCooldown = Math.max(0, this.surfaceLatchCooldown - dt);

    const anyHeld = heldL || heldR;

    // --- Cone aim search (runs whenever the button is held). ---
    if (anyHeld) {
      const Gc = CONFIG.character.grab;
      // If we already have an attached victim, prefer to keep aiming at that
      // character — the other hand should hunt nearby bones on the SAME body.
      const anchored = this.active.length > 0 ? this.active[0]!.victimCharacter : null;
      this.aim = this.findConeTarget(forwardX, forwardZ, anchored);
      if (this.aim) {
        // Target locked — reset the cone so the next re-engage starts narrow.
        this.coneHalfAngle = Gc.coneMinHalfAngleDeg * DEG2RAD;
      } else {
        // Nothing in cone — widen for next tick, up to the cap.
        const maxRad = Gc.coneMaxHalfAngleDeg * DEG2RAD;
        this.coneHalfAngle = Math.min(maxRad, this.coneHalfAngle + Gc.coneWidenDegPerSec * DEG2RAD * dt);
      }
    } else {
      this.aim = null;
      this.coneHalfAngle = CONFIG.character.grab.coneMinHalfAngleDeg * DEG2RAD;
    }

    // --- Try to latch each hand that wants to grab. ---
    // Latch-cooldown window: refuse all new latches briefly (e.g. just threw).
    if (this.active.length < 2 && this.latchCooldown <= 0) {
      const sides: Array<'L' | 'R'> = ['L', 'R'];
      for (const side of sides) {
        const wantHeld = side === 'L' ? heldL : heldR;
        if (!wantHeld) continue;
        if (this.active.find(a => a.side === side)) continue;

        // Punch-gates-second-hand: if one hand is already stuck and Punch
        // is held, the free hand is reserved for punching.
        if (this.active.length === 1 && punchHeld) continue;

        const handBone = side === 'L' ? Bone.LowerArmL : Bone.LowerArmR;
        const handBody = this.ragdoll.body(handBone);
        const handPos = handBody.translation();

        // Search the bones of the preferred victim first (aim target's
        // character), then fall back to any opponent body within grabReach.
        let bestBody: RAPIER.RigidBody | null = null;
        let bestVictim: Character | null = null;
        const grabReach = CONFIG.character.grabReach;
        let bestDist2 = grabReach * grabReach;

        const preferChar = this.aim?.victim ?? (this.active.length > 0 ? this.active[0]!.victimCharacter : null);
        if (preferChar) {
          for (const b of preferChar.ragdoll.handles.bodies) {
            const p = b.translation();
            const d2 = (p.x - handPos.x) ** 2 + (p.y - handPos.y) ** 2 + (p.z - handPos.z) ** 2;
            if (d2 < bestDist2) { bestDist2 = d2; bestBody = b; bestVictim = preferChar; }
          }
        }
        if (!bestBody) {
          for (const b of this.getOpponentBodies()) {
            const p = b.translation();
            const d2 = (p.x - handPos.x) ** 2 + (p.y - handPos.y) ** 2 + (p.z - handPos.z) ** 2;
            if (d2 < bestDist2) { bestDist2 = d2; bestBody = b; bestVictim = this.lookupCharacter(b); }
          }
        }
        // Surface fallback: when no character cone target was found this tick
        // AND no character body is within hand reach, the hand may latch onto
        // the nearest non-character rigid body (walls, ropes, asteroids) within
        // `searchRadius`. Cone-aim wins over surfaces by design — incidental
        // wall contact must not steal a hand from a tracked opponent.
        if (!bestBody && this.aim == null && this.surfaceLatchCooldown <= 0) {
          const surface = this.findSurfaceBody(handBody, handPos);
          if (surface) {
            bestBody = surface;
            bestVictim = null;
          }
        }
        if (!bestBody) continue;

        // Post-wriggle lock: block second-hand latches on the same victim.
        if (
          this.active.length === 1 &&
          this.active[0]!.victimCharacter === bestVictim &&
          bestVictim != null &&
          bestVictim.postWriggleLock > 0
        ) {
          continue;
        }

        // Anchors: hand uses its own center, target uses the local offset
        // that matches the current hand position → no snap on latch.
        const targetPos = bestBody.translation();
        const localTarget = {
          x: handPos.x - targetPos.x,
          y: handPos.y - targetPos.y,
          z: handPos.z - targetPos.z
        };
        const jd = RAPIER.JointData.spherical({ x: 0, y: 0, z: 0 }, localTarget);
        const joint = this.world.createImpulseJoint(jd, handBody, bestBody, true);
        this.active.push({ side, joint, victimBody: bestBody, victimCharacter: bestVictim });
      }
    }

    // --- Double-hand hold: suspend the victim in front of the grabber. ---
    // The spherical joints alone can't fight the victim's weight — arms lose
    // tension and the victim slumps. We cancel gravity on every victim body
    // (makes them weightless) and then PD-pull the victim's PELVIS toward a
    // target pose (in front of + above the grabber's torso). This reads as
    // "super-strong lift" without launching them into space.
    if (this.active.length === 2) {
      const v0 = this.active[0]!.victimCharacter;
      const v1 = this.active[1]!.victimCharacter;
      if (v0 && v0 === v1) {
        const Gc = CONFIG.character.grab;
        const gAbs = Math.abs(CONFIG.sim.gravity);

        // 1) Cancel gravity on all victim bones (×liftUpMul for a gentle lift
        //    bias so they settle slightly above equilibrium).
        const cancelAccel = gAbs * Gc.liftUpMul;
        for (const b of v0.ragdoll.handles.bodies) {
          const m = b.mass();
          b.applyImpulse({ x: 0, y: cancelAccel * m * dt, z: 0 }, true);
        }

        // 2) PD on victim pelvis toward a held-out-front target pose.
        const torso = this.ragdoll.body(Bone.Torso).translation();
        const fxu = forwardX, fzu = forwardZ; // already unit from update()
        const targetX = torso.x + fxu * Gc.liftForwardOffset;
        const targetY = torso.y + Gc.liftUpOffset;
        const targetZ = torso.z + fzu * Gc.liftForwardOffset;

        const pelvis = v0.ragdoll.pelvis();
        const p = pelvis.translation();
        const vel = pelvis.linvel();
        const m = pelvis.mass();
        const kp = Gc.liftKp;
        const kd = Gc.liftKd;
        const fx2 = (kp * (targetX - p.x) - kd * vel.x) * m * dt;
        const fy2 = (kp * (targetY - p.y) - kd * vel.y) * m * dt;
        const fz2 = (kp * (targetZ - p.z) - kd * vel.z) * m * dt;
        pelvis.applyImpulse({ x: fx2, y: fy2, z: fz2 }, true);
      }
    }

    // --- Sync victim bookkeeping: notify victims on attach/detach change. ---
    const nowVictims = new Set<Character>();
    for (const a of this.active) if (a.victimCharacter) nowVictims.add(a.victimCharacter);
    for (const v of nowVictims) {
      if (!this.heldVictims.has(v)) v.onAttackerAttached();
    }
    for (const v of this.heldVictims) {
      if (!nowVictims.has(v)) v.onAttackerReleased();
    }
    this.heldVictims = nowVictims;
  }

  private findConeTarget(forwardX: number, forwardZ: number, prefer: Character | null): GrabAim | null {
    const Gc = CONFIG.character.grab;
    const torsoPos = this.ragdoll.body(Bone.Torso).translation();
    const reach = Gc.coneReach;
    const cosHalf = Math.cos(this.coneHalfAngle);
    const fLen = Math.hypot(forwardX, forwardZ) || 1;
    const fx = forwardX / fLen, fz = forwardZ / fLen;

    // Once we've already attached to a victim, always aim at that victim's
    // closest body within the (wider) reach — bypassing the cone cone check
    // so the arm pose tracks the victim even if they swing behind us.
    if (prefer) {
      let bestBody: RAPIER.RigidBody | null = null;
      let bestDist2 = reach * reach * 4; // generous — 2×reach for tracking
      for (const b of prefer.ragdoll.handles.bodies) {
        const p = b.translation();
        const dx = p.x - torsoPos.x, dy = p.y - torsoPos.y, dz = p.z - torsoPos.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < bestDist2) { bestDist2 = d2; bestBody = b; }
      }
      if (bestBody) return { body: bestBody, victim: prefer };
    }

    let bestBody: RAPIER.RigidBody | null = null;
    let bestVictim: Character | null = null;
    let bestDist2 = reach * reach;

    for (const b of this.getOpponentBodies()) {
      const p = b.translation();
      const dx = p.x - torsoPos.x, dy = p.y - torsoPos.y, dz = p.z - torsoPos.z;
      const dist2 = dx * dx + dy * dy + dz * dz;
      if (dist2 > reach * reach) continue;
      const horizLen = Math.hypot(dx, dz);
      if (horizLen < 1e-4) continue;
      const dot = (dx * fx + dz * fz) / horizLen;
      if (dot < cosHalf) continue;

      if (dist2 < bestDist2) {
        bestDist2 = dist2;
        bestBody = b;
        bestVictim = this.lookupCharacter(b);
      }
    }

    if (!bestBody) return null;
    return { body: bestBody, victim: bestVictim };
  }

  /** Find the nearest non-character rigid body within `surfaceGrab.searchRadius`
   *  of the hand. Used by the per-hand surface fallback. The shape query hits
   *  Rapier's broadphase BVH first, so cost is log-bounded in body count even
   *  on the spaceship map (480 asteroids). */
  private findSurfaceBody(
    handBody: RAPIER.RigidBody,
    handPos: { x: number; y: number; z: number }
  ): RAPIER.RigidBody | null {
    const radius = CONFIG.character.surfaceGrab.searchRadius;
    const ball = new RAPIER.Ball(radius);
    const identity = { x: 0, y: 0, z: 0, w: 1 };
    let best: RAPIER.RigidBody | null = null;
    let bestDist2 = Infinity;
    this.world.intersectionsWithShape(
      handPos,
      identity,
      ball,
      (collider) => {
        const body = collider.parent();
        if (!body) return true;
        // Skip every character bone (own + opponents). lookupCharacter is
        // populated with all character bodies in World.ts.
        if (this.lookupCharacter(body) !== null) return true;
        const p = body.translation();
        const d2 = (p.x - handPos.x) ** 2 + (p.y - handPos.y) ** 2 + (p.z - handPos.z) ** 2;
        if (d2 < bestDist2) { bestDist2 = d2; best = body; }
        return true; // keep iterating
      },
      undefined,
      ((1 << 16) | 0xffff),
      undefined,
      handBody
    );
    return best;
  }

  /** Release every joint whose target is a non-character body (surface).
   *  Character grabs are kept. Sets the surface-only re-latch cooldown so
   *  the player can't immediately re-grab the same wall mid-launch. Used by
   *  the pull-up release path. */
  releaseSurfaceHolds(): void {
    const keep: ActiveGrab[] = [];
    for (const a of this.active) {
      if (a.victimCharacter === null) this.world.removeImpulseJoint(a.joint, true);
      else keep.push(a);
    }
    this.active = keep;
    this.surfaceLatchCooldown = CONFIG.character.surfaceGrab.pullupCooldownSec;
  }

  /** Release every joint targeting `victim`. Used by the victim's wriggle
   *  escape path to forcibly break all grabs on itself. */
  releaseAllOn(victim: Character): void {
    const keep: ActiveGrab[] = [];
    for (const a of this.active) {
      if (a.victimCharacter === victim) this.world.removeImpulseJoint(a.joint, true);
      else keep.push(a);
    }
    this.active = keep;
    if (this.heldVictims.has(victim)) {
      this.heldVictims.delete(victim);
      victim.onAttackerReleased();
    }
  }

  /** Throw: apply impulse to grabbed bodies along forward, then release. */
  throwAll(forward: { x: number; y: number; z: number }, force = 32): void {
    for (const a of this.active) {
      const m = a.victimBody.mass();
      a.victimBody.applyImpulse({
        x: forward.x * force * m,
        y: 0.5 * force * m,
        z: forward.z * force * m
      }, true);
    }
    this.releaseAll();
  }

  /** Two-hand throw: launch the held victim's whole ragdoll up-and-forward,
   *  release both hands, and enter a brief latch-cooldown so the attacker
   *  can't immediately re-grab the victim mid-air. */
  throwDoubleHold(forward: { x: number; z: number }, fwdImpulse: number, upImpulse: number): void {
    const victim = this.doubleHoldVictim();
    if (!victim) return;
    // Limp victims have no PD resistance and eat the full Δv directly —
    // scale the throw down so a KO'd / stunned target doesn't get launched
    // across the arena.
    const limp = victim.knockedOut || victim.stunRemaining > 0;
    const mul = limp ? CONFIG.character.grab.throwRagdollMul : 1;
    const fwd = fwdImpulse * mul;
    const up = upImpulse * mul;
    // Distribute the launch across every bone of the victim — this way the
    // ragdoll rides out together instead of the pelvis leaving the torso
    // behind.
    for (const b of victim.ragdoll.handles.bodies) {
      const m = b.mass();
      b.applyImpulse({
        x: forward.x * fwd * m,
        y: up * m,
        z: forward.z * fwd * m
      }, true);
    }
    this.releaseAllOn(victim);
    this.latchCooldown = CONFIG.character.grab.throwReleaseCooldownSec;
  }

  releaseAll(): void {
    for (const a of this.active) {
      this.world.removeImpulseJoint(a.joint, true);
    }
    this.active = [];
    for (const v of this.heldVictims) v.onAttackerReleased();
    this.heldVictims.clear();
  }
}
